import React, { Children, ComponentProps, PropsWithChildren } from 'react';
import { StyleSheet } from 'react-native';
import { useStyle } from 'react-native-style-utilities';

import {
  HorizontalScrollRail,
  HStack,
  theme,
  useContainerSpacing,
  useResponsive,
} from '@sg/garnish';

import { HOME_PRODUCT_CARD_SIZES } from '../HomeProductCard';

// ─────────────────────────────────────────────────────────────────────────────

export const HomeGenericCategory = (props: ComponentProps<typeof HorizontalScrollRail>) => {
  const { children } = props;

  const { match, currentBreakpoint } = useResponsive();
  const containerSpacing = useContainerSpacing();
  const contentContainerStyles = useStyle(
    () => ({
      paddingHorizontal: containerSpacing,
      columnGap: theme.spacing['2'],
    }),
    [containerSpacing],
  );

  if (currentBreakpoint.isXS) {
    return (
      <HorizontalScrollRail
        outerOffset={0}
        showNavControls={false}
        gap={theme.spacing['2']}
        contentContainerStyle={contentContainerStyles}
      >
        {children}
      </HorizontalScrollRail>
    );
  }

  const childrenCount = Children.count(children);

  return (
    <HStack
      style={match([null, styles.categoriesSM])}
      itemsPerRow={childrenCount > 1 ? match([2, 2, 3]) : 1}
      gap={match([theme.spacing['2'], theme.spacing['6']])}
      itemStyle={styles.itemStyle}
    >
      {children}
    </HStack>
  );
};

export const HomeFilterableCategory = (props: PropsWithChildren) => {
  const { children } = props;

  const { match, screenDimensions } = useResponsive();
  const containerSpacing = useContainerSpacing();
  const categoryStyleXS = useStyle(() => ({ paddingHorizontal: containerSpacing }), [containerSpacing]); // prettier-ignore

  const childrenCount = Children.count(children);
  const shouldHaveOneItemPerRow = childrenCount === 1 || screenDimensions.width < 350;
  const itemsPerRow = shouldHaveOneItemPerRow ? 1 : match([2, 2, 3]);
  const productCardSize = match(HOME_PRODUCT_CARD_SIZES);
  const containerSize = itemsPerRow * (productCardSize + GRID_GAP) + GRID_PADDING * 2;
  const containerSizeStyle = useStyle(() => ({ width: containerSize }), [containerSize]);

  const categoryStyle = match([categoryStyleXS, styles.categoriesSM]);
  const filterableStyle = match([styles.filterableCategoryXS, styles.filterableCategorySM]);
  const containerStyles = [filterableStyle, categoryStyle, match([null, containerSizeStyle])];

  return (
    <HStack
      style={containerStyles}
      itemsPerRow={shouldHaveOneItemPerRow ? 1 : match([2, 2, 3])}
      gap={match([theme.spacing['2'], theme.spacing['6']])}
      itemStyle={styles.itemStyle}
    >
      {children}
    </HStack>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const GRID_GAP = theme.spacing['6'];
const GRID_PADDING = theme.spacing['10'];

const styles = StyleSheet.create({
  flexView: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing['2'],
  },
  categoriesSM: {
    width: '100%',
    alignSelf: 'center',
    marginLeft: 0,
    marginRight: 0,
    paddingHorizontal: GRID_PADDING,
    rowGap: theme.spacing['8'],
  },
  filterableCategoryXS: {
    paddingVertical: theme.spacing['6'],
  },
  filterableCategorySM: {
    paddingVertical: theme.spacing['6'],
  },
  itemStyle: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});
